import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import EventSeatingManager from '@/components/event-seating/EventSeatingManager';
import SeatMapStudio from '@/components/seatmap-studio/SeatMapStudio';
import type { Plan } from '@/components/seatmap-studio/types';
import { fetchVenue, uploadSeatmap } from '@/api/venueApi';
import { DialogProvider, useDialogs } from '@/components/seatmap-studio/dialogs';
import { IconLock as Lock, IconInfoCircle as InfoCircle } from '@tabler/icons-react';

export const EDITING_LOCKED_MESSAGE = 'Editing is locked because tickets have already been sold for this event.';
export const EDITING_LOCK_NOTICE = 'The venue map and tiers become locked once the first ticket is sold. Set up your seat map and tiers before ticket sales begin.';

const hasSeats = (seatMap: any) => (seatMap?.zones ?? []).some((zone: any) => (zone?.rows ?? []).some((row: any) => (row?.seats ?? []).length > 0));

const uploadErrorMessage = (error: any) => {
    const status = error?.response?.status;
    if (status === 413 || error?.response?.data?.message?.includes('too large')) {
        return 'Seatmap file is too large to upload. Please try reducing the complexity of your seatmap.';
    }
    if (status === 409 && error?.response?.data?.message) return error.response.data.message;
    if (status === 400) return 'Invalid seatmap file format. Please check your seatmap and try again.';
    if (status === 500) return 'Server error occurred while uploading seatmap. Please try again later.';
    return 'Error uploading seatmap. Please try again.';
};

const SeatMapEditorContent: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const dialogs = useDialogs();
    const venueId = searchParams.get('venue');

    const { data: venueResponse, isLoading } = useQuery({
        queryKey: ['venue', venueId],
        queryFn: () => fetchVenue(venueId!),
        enabled: Boolean(venueId),
        refetchInterval: 30000,
    });
    const venue = venueResponse?.data;

    const uploadMutation = useMutation({
        mutationFn: ({ id, file, confirmTierPriceRemoval }: { id: string; file: File; confirmTierPriceRemoval?: boolean }) => uploadSeatmap(id, file, confirmTierPriceRemoval),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['venue', venueId] });
            queryClient.invalidateQueries({ queryKey: ['venues'] });
            queryClient.invalidateQueries({ queryKey: ['tierSummary'] });
            queryClient.invalidateQueries({ queryKey: ['tierSummaryDrawer'] });
        },
    });

    const handleSave = async (plan: Plan) => {
        if (!venueId) return;
        const file = new File([JSON.stringify(plan)], 'seatmap.json', { type: 'application/json' });
        const sizeInMB = file.size / (1024 * 1024);
        if (sizeInMB > 1 && !(await dialogs.confirm({ title: 'Large seat map', message: `Your seatmap file is ${sizeInMB.toFixed(2)} MB. Large files may take longer to upload and could fail. Do you want to continue?` }))) {
            throw new Error('cancelled');
        }
        try {
            await uploadMutation.mutateAsync({ id: venueId, file });
        } catch (error: any) {
            if (error?.response?.status === 409 && error?.response?.data?.code === 'TIER_PRICES_LINKED') {
                if (!(await dialogs.confirm({ title: 'Remove linked ticket prices', message: error.response.data.message, confirmLabel: 'Continue', destructive: true }))) throw new Error('cancelled');
                try {
                    await uploadMutation.mutateAsync({ id: venueId, file, confirmTierPriceRemoval: true });
                    return;
                } catch (retryError) {
                    await dialogs.alert(uploadErrorMessage(retryError));
                    throw retryError;
                }
            }
            if (error?.response?.data?.code === 'VENUE_EDITING_LOCKED') {
                queryClient.invalidateQueries({ queryKey: ['venue', venueId] });
                queryClient.invalidateQueries({ queryKey: ['venues'] });
            }
            await dialogs.alert(uploadErrorMessage(error));
            throw error;
        }
    };

    const eventId = searchParams.get('event');
    const [mode, setMode] = useState<'map' | 'holds'>(searchParams.get('mode') === 'holds' && eventId ? 'holds' : 'map');
    const returnTo = searchParams.get('returnTo');
    const safeReturnTo = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : null;
    const goBack = () => {
        if (safeReturnTo) navigate(safeReturnTo);
        else if (window.history.length > 1) navigate(-1);
        else navigate('/venue-management');
    };

    if (venueId && isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white dark:bg-nightBg">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary40 dark:border-nightAccent mx-auto mb-3" />
                    <p className="text-sm text-grey40 dark:text-nightMuted">Loading venue...</p>
                </div>
            </div>
        );
    }

    if (venue && venue.canEdit === false) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-white dark:bg-nightBg">
                <Lock className="w-12 h-12 text-primary40 dark:text-nightAccent" />
                <h1 className="text-xl font-semibold text-[#222] dark:text-nightTextSecondary">View only</h1>
                <p className="text-sm text-grey40 dark:text-nightMuted max-w-sm">
                    {venue.name} belongs to another organization. Only its owner, admins and event managers can edit its seat map. You can still use it for your events.
                </p>
                <button
                    type="button"
                    onClick={goBack}
                    className="px-6 py-2 rounded bg-primary40 text-white font-semibold dark:bg-nightAccent dark:text-[#080808]"
                >
                    Go Back
                </button>
            </div>
        );
    }

    const tabClass = (active: boolean) => `h-9 px-4 rounded-full text-sm font-semibold whitespace-nowrap ${active ? 'bg-primary40 text-white dark:bg-nightAccent dark:text-[#080808]' : 'text-[#444] dark:text-nightTextSecondary hover:bg-gray-100 dark:hover:bg-white/10'}`;

    return (
        <div className="h-[100dvh] flex flex-col">
            {eventId && venue && (
                <div role="tablist" aria-label="Venue map sections" className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 dark:border-nightBorder bg-white dark:bg-nightBg">
                    <button type="button" role="tab" aria-selected={mode === 'map'} className={tabClass(mode === 'map')} onClick={() => setMode('map')}>Map and tiers</button>
                    <button type="button" role="tab" aria-selected={mode === 'holds'} className={tabClass(mode === 'holds')} onClick={() => setMode('holds')}>Holds</button>
                    <button type="button" className="ml-auto text-sm underline text-[#444] dark:text-nightTextSecondary" onClick={goBack}>Back to tickets</button>
                </div>
            )}
            {mode === 'holds' && eventId && venue ? (
                <div className="flex-1 overflow-y-auto p-4">
                    <p className="mb-3 text-xs text-grey40 dark:text-nightMuted">Holds apply to this event and use the saved venue map. Save map changes first.</p>
                    <EventSeatingManager eventId={eventId} venue={venue} tickets={[]} holdsOnly />
                </div>
            ) : (
            <>
            {venue && !venue.editingLocked && !hasSeats(venue.seatMap) && (
                <div className="flex items-start gap-2 border-b border-gray-100 bg-primary99 px-4 py-2.5 text-xs text-primary20 dark:border-nightBorder dark:bg-white/[0.06] dark:text-nightAccent" role="note">
                    <InfoCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{EDITING_LOCK_NOTICE}</span>
                </div>
            )}
            <div className="flex-1 min-h-0">
            <SeatMapStudio
                key={venue?._id ?? 'standalone'}
                initialPlan={venue?.seatMap}
                onSave={venueId ? handleSave : undefined}
                onBack={goBack}
                title={venue ? venue.name : 'Seat map editor'}
                subtitle={venue?.address?.fullAddress}
                showExport
                editingLockedMessage={venue?.editingLocked ? EDITING_LOCKED_MESSAGE : undefined}
                lockedSeatsMessage={venue?.seatsLocked ? `${venue.soldSeatCount} seat${venue.soldSeatCount === 1 ? ' has' : 's have'} been sold for events at this venue. You can add or move seats, but seats cannot be deleted.` : undefined}
            />
            </div>
            </>
            )}
        </div>
    );
};

const SeatMapEditorScreen: React.FC = () => (
    <DialogProvider>
        <SeatMapEditorContent />
    </DialogProvider>
);

export default SeatMapEditorScreen;
